import { Image } from '../../../domain/entities/Image';
import { LoggedUser } from '../../../domain/entities/User';
import { getLoggedUser } from '../auth/endpoints';
import { createNewEndpoint } from '../core/endpoint';
import { GraphQlMethods } from '../core/fetch';
import { PreventUnauthenticatedGuard } from '../core/guards/preventUnauthenticated';
import { Interceptor } from '../core/interceptors';
import { ResponseResult } from '../core/request';
import {
  getUsersFollowedByGivenUser,
  getUsersFollowedByCurrentUser,
  getGivenUserFollowers,
  FOLLOW_QUERY_LIMIT,
} from '../followers/endpoints';
import { userOfferVotes } from '../votes/endpoints';
import { UserActionType } from './actionTypes';
import { CONFIRM_ACCOUNT_DELETION_MUTATION } from './mutations/confirmAccountDeletion';
import UPDATE_AVATAR from './mutations/updateAvatar';
import UPDATE_NICKNAME from './mutations/updateNickname';
import UPDATE_USER_PREFERENCE from './mutations/updateUserPreference';
import GET_COMMENTS_BY_NICKNAME_QUERY from './queries/getCommentsByNickname';
import GET_CURRENT_USER_COMMENTS_QUERY from './queries/getCurrentUserComments';
import GET_CURRENT_USER_DEALS_QUERY from './queries/getCurrentUserDeals';
import GET_USER_DEALS_BY_NICKNAME from './queries/getDealsByNickname';
import GET_PRIVATE_PROFILE_USER_QUERY from './queries/getPrivateProfile';
import GET_PROFILE_USER_BY_NICKNAME from './queries/getProfileUserByNickname';
import GET_USER_QUERY from './queries/getUser';
import GET_USER_PREFERENCE_QUERY from './queries/getUserPreference';
import {
  ConfirmAccountDeletionResponse,
  ConfirmAccountDeletionVariables,
} from './types/ConfirmAccountDeletion';
import {
  GetCommentsByNicknameCache,
  GetCommentsByNicknameResponse,
  GetCommentsByNicknameResult,
  GetCommentsByNicknameVariables,
} from './types/GetCommentsByNickname';
import {
  GetCurrentUserCommentsCache,
  GetCurrentUserCommentsResponse,
  GetCurrentUserCommentsResult,
  GetCurrentUserCommentsVariables,
} from './types/GetCurrentUserComments';
import {
  GetCurrentUserDealsCache,
  GetCurrentUserDealsResponse,
  GetCurrentUserDealsResult,
  GetCurrentUserDealsVariables,
} from './types/GetCurrentUserDeals';
import {
  GetDealsByNicknameCache,
  GetDealsByNicknameResponse,
  GetDealsByNicknameResult,
  GetDealsByNicknameVariables,
} from './types/GetDealsByNickname';
import {
  GetPrivateProfileCache,
  GetPrivateProfileResponse,
  GetPrivateProfileResult,
  GetPrivateProfileVariables,
} from './types/GetPrivateProfile';
import {
  GetProfileUserByNicknameResponse,
  GetProfileUserByNicknameResult,
  GetProfileUserByNicknameResultCache,
  GetProfileUserByNicknameVariables,
} from './types/GetProfileUserByNickname';
import { GetUserResponse, GetUserVariables } from './types/GetUser';
import {
  GetUserPreferenceResponse,
  UserPreference,
  UserPreferenceCache,
} from './types/GetUserPreference';
import {
  UpdateAvatarMutationResponse,
  UpdateAvatarMutationVariables,
} from './types/UpdateAvatar';
import {
  UpdateNicknameMutationResponse,
  UpdateUserNicknameMutationVariables,
} from './types/UpdateNickname';
import {
  UpdateUserPreferenceMutationResponse,
  UpdateUserPreferenceMutationVariables,
} from './types/UpdateUserPreference';

const deduplicateDeals = <Deal extends { id: string }>(
  deals: Deal[] = []
): Deal[] => {
  const dealsObjs = deals.reduce(
    (acc, curr) => ({ ...acc, [curr.id]: curr }),
    {} as Record<string, Deal>
  );
  return Object.keys(dealsObjs).map((dealId) => dealsObjs[dealId]);
};

// TODO: [sc-18710] Separar endpoint de perfil publico e perfil privado
const getProfileByNicknameInterceptor: Interceptor<
  ResponseResult<GetProfileUserByNicknameResult, unknown>,
  unknown
> = ({ incoming: { data, loading } }) => {
  if (!data || loading) return;

  const userId = data.id;

  getUsersFollowedByGivenUser.requestAsPromise({
    userId,
    limit: FOLLOW_QUERY_LIMIT,
  });
  getUsersFollowedByCurrentUser.requestAsPromise({ userIds: [userId] });
  getGivenUserFollowers.requestAsPromise({ userId, limit: FOLLOW_QUERY_LIMIT });
};

export const getProfileUserByNickname = createNewEndpoint<
  GetProfileUserByNicknameResponse,
  GetProfileUserByNicknameVariables,
  GetProfileUserByNicknameResult,
  GetProfileUserByNicknameResultCache
>({
  id: UserActionType.GetProfileUserByNickname,
  query: GET_PROFILE_USER_BY_NICKNAME,
  method: GraphQlMethods.Get,
  interceptors: [getProfileByNicknameInterceptor],
  transform: (response) => response.public.userByNickname,
  cacheTransform: ({ loading, data }) => ({
    loading,
    profile: data,
  }),
});

export const getDealsByNickname = createNewEndpoint<
  GetDealsByNicknameResponse,
  GetDealsByNicknameVariables,
  GetDealsByNicknameResult,
  GetDealsByNicknameCache
>({
  id: UserActionType.GetDealsByNickname,
  query: GET_USER_DEALS_BY_NICKNAME,
  method: GraphQlMethods.Get,
  transform: (response) => response.public.userByNickname.offers,
  cacheTransform: ({ loading, data, variables }, currentValue) => ({
    currentNickname: variables?.nickname,
    loading: variables?.offersAfter ? loading : !data,
    paginationLoading: !!variables?.offersAfter && loading,
    pageInfo: data?.pageInfo,
    deals:
      currentValue?.currentNickname === variables?.nickname
        ? [...(currentValue?.deals || []), ...(data?.edges || [])]
        : data?.edges || [],
  }),
});

export const getCommentsByNickname = createNewEndpoint<
  GetCommentsByNicknameResponse,
  GetCommentsByNicknameVariables,
  GetCommentsByNicknameResult,
  GetCommentsByNicknameCache
>({
  id: UserActionType.getCommentsByNickname,
  query: GET_COMMENTS_BY_NICKNAME_QUERY,
  method: GraphQlMethods.Get,
  transform: (response) => response.public.userByNickname.comments,
  cacheTransform: ({ loading, data, variables }, currentValue) => ({
    currentNickname: variables?.nickname,
    loading: variables?.commentsAfter ? loading : !data,
    paginationLoading: !!variables?.commentsAfter && loading,
    pageInfo: data?.pageInfo,
    comments:
      currentValue?.currentNickname === variables?.nickname
        ? [...(currentValue?.comments || []), ...(data?.edges || [])]
        : data?.edges || [],
  }),
});

export const updateAvatar = createNewEndpoint<
  UpdateAvatarMutationResponse,
  UpdateAvatarMutationVariables,
  Image,
  void
>({
  id: UserActionType.UpdateAvatar,
  query: UPDATE_AVATAR,
  method: GraphQlMethods.Post,
  transform: (response) => response.me.updateUserProfile.image,
  cacheTransform: ({ data }) => {
    const profileCache = getProfileUserByNickname.cache$.value;
    const loggedUserCache = getLoggedUser.cache$.value;

    if (!data) return;

    if (profileCache?.profile) {
      const nextProfile: GetProfileUserByNicknameResult = {
        ...profileCache.profile,
        image: data,
      };

      getProfileUserByNickname.cache$.next({
        ...profileCache,
        profile: nextProfile,
      });
    }

    if (loggedUserCache?.loggedUser) {
      const nextLoggedUser: LoggedUser = {
        ...loggedUserCache?.loggedUser,
        image: data,
      };

      getLoggedUser.cache$.next({
        ...loggedUserCache,
        loggedUser: nextLoggedUser,
      });
    }
  },
});

export const updateNickname = createNewEndpoint<
  UpdateNicknameMutationResponse,
  UpdateUserNicknameMutationVariables,
  string,
  void
>({
  id: UserActionType.UpdateNickname,
  query: UPDATE_NICKNAME,
  method: GraphQlMethods.Post,
  transform: (response) => response.me.updateUserProfile.nickname,
  cacheTransform: ({ data }) => {
    const profileCache = getProfileUserByNickname.cache$.value;
    const loggedUserCache = getLoggedUser.cache$.value;

    if (!data) return;

    if (profileCache?.profile) {
      const nextProfile: GetProfileUserByNicknameResult = {
        ...profileCache.profile,
        nickname: data,
      };

      getProfileUserByNickname.cache$.next({
        ...profileCache,
        profile: nextProfile,
      });
    }
    if (loggedUserCache?.loggedUser) {
      const nextLoggedUser: LoggedUser = {
        ...loggedUserCache?.loggedUser,
        nickname: data,
      };

      getLoggedUser.cache$.next({
        ...loggedUserCache,
        loggedUser: nextLoggedUser,
      });
    }
  },
});

export const getUser = createNewEndpoint<
  GetUserResponse,
  GetUserVariables,
  string
>({
  id: UserActionType.GetUser,
  query: GET_USER_QUERY,
  method: GraphQlMethods.Get,
  transform: (response) => response.public.user.nickname,
});

export const getUserPreference = createNewEndpoint<
  GetUserPreferenceResponse,
  void,
  UserPreference,
  UserPreferenceCache
>({
  id: UserActionType.GetUserPreference,
  query: GET_USER_PREFERENCE_QUERY,
  method: GraphQlMethods.Get,
  transform: (response) => response.me.userPreference,
  cacheTransform: ({ data }) => ({ userPreference: data }),
});

export const updateUserPreference = createNewEndpoint<
  UpdateUserPreferenceMutationResponse,
  UpdateUserPreferenceMutationVariables,
  UserPreference,
  void
>({
  id: UserActionType.UpdateUserPreference,
  query: UPDATE_USER_PREFERENCE,
  method: GraphQlMethods.Post,
  transform: (response) => response.me.updateUserPreference,
  cacheTransform: ({ data, loading }) => {
    if (!data || loading) return;

    const userPreferenceCache = getUserPreference.cache$.value;

    if (userPreferenceCache) {
      getUserPreference.cache$.next({
        ...userPreferenceCache,
        userPreference: data,
      });
    }
  },
});

// Private profile

const getCommentsVotesInterceptor: Interceptor<
  ResponseResult<GetCurrentUserCommentsResult, unknown>,
  unknown
> = ({ incoming: { data, loading } }) => {
  if (!data || loading) return;
  const offerIds = data.edges.map((comment) => comment.offer.id);
  userOfferVotes.requestAsPromise({ offerIds });
};

export const getCurrentUserComments = createNewEndpoint<
  GetCurrentUserCommentsResponse,
  GetCurrentUserCommentsVariables,
  GetCurrentUserCommentsResult,
  GetCurrentUserCommentsCache
>({
  id: UserActionType.GetCurrentUserComments,
  query: GET_CURRENT_USER_COMMENTS_QUERY,
  method: GraphQlMethods.Get,
  guards: [PreventUnauthenticatedGuard],
  interceptors: [getCommentsVotesInterceptor],
  transform: (response) => response.me.getUserInfo.comments,
  cacheTransform: ({ variables, data, loading }, current) => {
    const comments = deduplicateDeals([
      ...(current?.comments || []),
      ...(data?.edges || []),
    ]);

    return {
      loading: variables?.commentsAfter ? loading : !data,
      paginationLoading: !!variables?.commentsAfter && loading,
      pageInfo: data?.pageInfo,
      comments,
    };
  },
});

const getDealsVotesInterceptor: Interceptor<
  ResponseResult<GetCurrentUserDealsResult, unknown>,
  unknown
> = ({ incoming: { data, loading } }) => {
  if (!data || loading) return;
  const offerIds = data.edges.map((deal) => deal.id);
  userOfferVotes.requestAsPromise({ offerIds });
};

export const getCurrentUserDeals = createNewEndpoint<
  GetCurrentUserDealsResponse,
  GetCurrentUserDealsVariables,
  GetCurrentUserDealsResult,
  GetCurrentUserDealsCache
>({
  id: UserActionType.GetCurrentUserDeals,
  query: GET_CURRENT_USER_DEALS_QUERY,
  method: GraphQlMethods.Get,
  guards: [PreventUnauthenticatedGuard],
  interceptors: [getDealsVotesInterceptor],
  transform: (response) => response.me.getUserInfo.offers,
  cacheTransform: ({ variables, data, loading }, current) => {
    const deals = deduplicateDeals([
      ...(current?.deals || []),
      ...(data?.edges || []),
    ]);

    return {
      loading: variables?.offersAfter ? loading : !data,
      paginationLoading: !!variables?.offersAfter && loading,
      pageInfo: data?.pageInfo,
      deals,
    };
  },
});

const getPrivateProfileInterceptor: Interceptor<
  ResponseResult<GetPrivateProfileResult, unknown>,
  unknown
> = ({ incoming: { data, loading } }) => {
  if (!data || loading) return;

  const userId = data.id;

  getUsersFollowedByGivenUser.requestAsPromise({
    userId,
    limit: FOLLOW_QUERY_LIMIT,
  });
  getUsersFollowedByCurrentUser.requestAsPromise({ userIds: [userId] });
  getGivenUserFollowers.requestAsPromise({ userId, limit: FOLLOW_QUERY_LIMIT });

  getCurrentUserComments.requestAsPromise({});
  getCurrentUserDeals.requestAsPromise({});
};

export const getPrivateProfile = createNewEndpoint<
  GetPrivateProfileResponse,
  GetPrivateProfileVariables,
  GetPrivateProfileResult,
  GetPrivateProfileCache
>({
  id: UserActionType.GetPrivateProfile,
  query: GET_PRIVATE_PROFILE_USER_QUERY,
  method: GraphQlMethods.Get,
  guards: [PreventUnauthenticatedGuard],
  interceptors: [getPrivateProfileInterceptor],
  transform: (response) => response.me.getUserInfo,
  cacheTransform: ({ data, loading }) => {
    if (!data) return {};
    return {
      loading,
      profile: data,
    };
  },
});

export const confirmAccountDeletion = createNewEndpoint<
  ConfirmAccountDeletionResponse,
  ConfirmAccountDeletionVariables
>({
  id: UserActionType.ConfirmAccountDeletion,
  query: CONFIRM_ACCOUNT_DELETION_MUTATION,
  method: GraphQlMethods.Post,
});
